import {
  getExtensionFromMimeType,
  getSizeInBytes,
  normalizeUploadFile,
} from '@shared';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import {
  FileError,
  FileUploadState,
  FileUploadStateFile,
  StateError,
} from './file-upload-v2.interfaces';

const toValidatedFiles = (
  files: Record<string, FileUploadStateFile>,
  acceptedFilesTypes: string[],
) => {
  return Object.fromEntries(
    Object.values(files).map((file) => {
      /**
       * `upload-failed` and `upload-done` entries are returned as-is (no
       * re-validation). Failed entries keep their upload error; done entries are
       * either freshly-uploaded chat files (already passed validation) or
       * edit-mode seeded persisted documents — re-running the extension/size
       * checks on a seeded doc would flip a legacy-extension or over-limit
       * document to `upload-invalid` the moment the user touches the zone, making
       * a previously-valid skill un-editable.
       */
      if (file.status !== 'upload-failed' && file.status !== 'upload-done') {
        let error: FileError = null;

        if (Validators.isFileExtensionInvalid(file, acceptedFilesTypes)) {
          error = 'Invalid file type';
        } else if (Validators.isFileEmpty(file)) {
          error = 'Empty file';
        } else if (Validators.isFileSizeExceeded(file)) {
          error = 'File size exceeded';
        }

        return [
          file.uuid,
          {
            ...file,
            status: error ? 'upload-invalid' : file.status,
            error,
          },
        ];
      }
      /**
       * Failed / done entries are returned unchanged.
       */
      return [file.uuid, file];
    }),
  );
};

const toStateError = (
  files: Record<string, Pick<FileUploadStateFile, 'error' | 'size'>>,
): StateError => {
  const isAnyFileInvalid = Validators.isAnyFileInvalid(files);

  let error: StateError = null;
  if (isAnyFileInvalid) {
    error = 'Some file(s) are invalid';
  }
  return error;
};
const toLoading = (
  files: Record<string, Pick<FileUploadStateFile, 'status'>>,
) => {
  return Object.values(files).some((file) => file.status === 'upload-ongoing');
};

const toValid = (state: Pick<FileUploadState, 'files' | 'error'>) => {
  return (
    state.error === null &&
    Object.values(state.files).every((file) => file.error === null)
  );
};

const toUploaded = (
  files: Record<string, Pick<FileUploadStateFile, 'status'>>,
) => {
  return Object.values(files).every((file) => file.status === 'upload-done');
};

export const Mappers = {
  toFileUploadStateFile: (rawFile: File): FileUploadStateFile => {
    // Canonicalize at the single staging chokepoint (both the picker and
    // drag-drop funnel through here, and `file` below is the multipart body
    // that gets uploaded) so a `.jfif` is renamed to `.jpg` on the object
    // itself — the drop-zone key, the from-chat filename, and everything
    // downstream then only ever see canonical jpg/jpeg/png (SB-3271).
    const file = normalizeUploadFile(rawFile);
    return {
      transactionId: uuidv4(),
      uuid: uuidv4(),
      name: file.name,
      extension: getExtensionFromMimeType(file.type) ?? 'n/a',
      size: file.size,
      file,
      status: 'upload-pending',
      error: null,
    };
  },
  toValidatedState: (state: FileUploadState, supportedExtensions: string[]) => {
    state.files = toValidatedFiles(state.files, supportedExtensions);
    state.error = toStateError(state.files);
    state.valid = toValid(state);
    state.loading = toLoading(state.files);
    state.uploaded = toUploaded(state.files);
    return state;
  },
};

export const Validators = {
  isAnyFileInvalid: (
    files: Record<string, Pick<FileUploadStateFile, 'error'>>,
  ): boolean => {
    return Object.values(files).some((file) => file.error !== null);
  },
  isFileExtensionInvalid: (
    file: Pick<FileUploadStateFile, 'extension'>,
    supportedExtensions: string[],
  ): boolean => {
    return !supportedExtensions.includes(file.extension);
  },
  isFileSizeExceeded: (file: Pick<FileUploadStateFile, 'size'>): boolean => {
    return file.size > getSizeInBytes(environment.maxFileUploadSizeInMB);
  },
  isFileEmpty: (file: Pick<FileUploadStateFile, 'size'>): boolean => {
    return file.size === 0;
  },
};
